import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { generatePath, useNavigate } from 'react-router-dom';
import EditPencilIcon from '@mui/icons-material/BorderColorOutlined';
// import DeleteIcon from '@mui/icons-material/DeleteOutlineOutlined';
import DevicesOutlinedIcon from '@mui/icons-material/DevicesOutlined';
import EmailOutlinedIcon from '@mui/icons-material/EmailOutlined';
import HistoryIcon from '@mui/icons-material/History';
import LockOpen from '@mui/icons-material/LockOpen';
import QuestionMarkOutlinedIcon from '@mui/icons-material/QuestionMarkOutlined';
import EyeIcon from '@mui/icons-material/RemoveRedEyeOutlined';
import ToggleOffIcon from '@mui/icons-material/ToggleOff';
import ToggleOnIcon from '@mui/icons-material/ToggleOn';
import { useAppDispatch, useAppSelector } from '../../app/hooks';
import {
  useChangeInterviewModeToClinicianMutation,
  useCheckResultInProgressOfPacketMutation,
  useSendInterviewEmailOrTextTheQuestionsMutation,
  useToggleInterviewResultActivationMutation,
  useUnlockReportMutation,
} from '../../app/services/interview';
import {
  useChangeMonitorModeToClinicianMutation,
  useSendMonitorEmailOrTextTheQuestionsMutation,
  useToggleTrackerResultActivationMutation,
} from '../../app/services/monitor';
import { useToast } from '../../app/toast';
import PopupModal from '../../common/components/PopupModal';
import EditInterviewIcon from '../../common/components/icons/EditInterviewIcon';
import { PageStatus, setPageStatus } from '../../common/slices/globalSlice';
import { AssessmentStatus } from '../../common/utils/assessmentStatus';
import { AssessmentType } from '../../common/utils/assessmentType';
import { InterviewMode } from '../../common/utils/interviewsMode';
import { logger } from '../../common/utils/logger';
import { Role } from '../../common/utils/loginRole';
import { MonitorModeName } from '../../common/utils/monitorsMode';
import { PatientStatus } from '../../common/utils/patientStatus';
import {
  // canDeleteInterviewOrPatient,
  canDeactivateOrReactivateInterview,
  canConductAssessment,
} from '../../common/utils/permissionUtils';
import { DownloadReportRequest } from '../../types/InterviewReportRequest';
import { CheckResultInProgressOfPacketResponse } from '../../types/InterviewTypes';
import { AssessmentMode, LatestAssessmentItem, PatientInfo } from '../../types/PatientRequest';
import {
  ChangeInterviewModeToClinicianResponse,
  ChangeMonitorModeToClinicianResponse,
} from '../../types/PatientType';
import { selectUser } from '../auth/authSlice';
import { setInterviewMode, setTrialInterview } from '../interview/interviewSlice';
import { setMonitorMode } from '../monitor/monitorSlide';
import ContextMenu from './ContextMenu';
import useDownloadInterviewOrMonitorReport from './hooks/useDownloadInterviewOrMonitorReport';
import { clearAssessmentCachedData } from './patientUtils';

interface Props {
  patientInfo?: PatientInfo;
  assessmentItem: LatestAssessmentItem;
  patientId: string;
  assessmentId: number;
  status: string;
  type: AssessmentType;
  patientStatus?: string;
  reducedTypeId: number;
  hasInterval?: boolean;
  completedAt: Date;
  callShowPopup: () => Promise<void>;
  editExpireTimeStart?: Date;
  refetchData?: any;
  isSignature?: boolean;
}

interface PopupContent {
  title: string;
  description: string;
  btnOk: string;
  btnClose: string;
  toastMessage: string;
  hidePatient?: boolean;
}

export default function DashboardTableMenu({
  patientInfo,
  assessmentItem,
  patientId,
  assessmentId,
  status,
  type,
  patientStatus,
  reducedTypeId,
  hasInterval,
  completedAt,
  callShowPopup,
  editExpireTimeStart,
  refetchData,
  isSignature,
}: Props) {
  const { t } = useTranslation();
  const toast = useToast();
  const navigate = useNavigate();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const dispatch = useAppDispatch();
  const currentUser = useAppSelector(selectUser);
  const isAssessmentCompleted = status === AssessmentStatus.Completed;
  const isAssessmentEditing = status === AssessmentStatus.InProgress && (assessmentItem.isEditing || false);
  const [isShowPacketPopup, setIsShowPacketPopup] = useState<boolean>(false);
  const [isShowToggleActivationPopup, setIsShowToggleActivationPopup] = useState<boolean>(false);
  const [continueHandler, setContinueHandler] = useState<any>(null);

  const deactivateInterviewContentPopup = {
    title: t('patient.deactivateInterview.title'),
    description: t('patient.deactivateInterview.description'),
    btnOk: t('patient.deactivateInterview.btnOk'),
    btnClose: t('patient.deactivateInterview.btnClose'),
    toastMessage: t('patient.deactivateInterview.toastMessage'),
    showReasonField: true,
    reasonLabel: t('patient.deactivateInterview.reasonLabel'),
  } as PopupContent;

  const reactivateInterviewContentPopup = {
    title: t('patient.reactivateInterview.title'),
    description: t('patient.reactivateInterview.description'),
    btnOk: t('patient.reactivateInterview.btnOk'),
    btnClose: t('patient.reactivateInterview.btnClose'),
    toastMessage: t('patient.reactivateInterview.toastMessage'),
  } as PopupContent;

  const deactivateTrackerContentPopup = {
    title: t('patient.deactivateTracker.title'),
    description: t('patient.deactivateTracker.description'),
    btnOk: t('patient.deactivateTracker.btnOk'),
    btnClose: t('patient.deactivateTracker.btnClose'),
    toastMessage: t('patient.deactivateTracker.toastMessage'),
    showReasonField: true,
    reasonLabel: t('patient.deactivateTracker.reasonLabel'),
  } as PopupContent;

  const reactivateTrackerContentPopup = {
    title: t('patient.reactivateTracker.title'),
    description: t('patient.reactivateTracker.description'),
    btnOk: t('patient.reactivateTracker.btnOk'),
    btnClose: t('patient.reactivateTracker.btnClose'),
    toastMessage: t('patient.reactivateTracker.toastMessage'),
  } as PopupContent;

  const [toggleActivationContent, setToggleActivationContent] = useState<PopupContent>(
    deactivateInterviewContentPopup
  );

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };
  const handleClose = () => {
    setAnchorEl(null);
  };
  const { startDownloadReport } = useDownloadInterviewOrMonitorReport(t);

  const [unlockReport] = useUnlockReportMutation();

  const [sendInterviewEmailOrTextTheQuestions] = useSendInterviewEmailOrTextTheQuestionsMutation();

  const [sendMonitorEmailOrTextTheQuestions] = useSendMonitorEmailOrTextTheQuestionsMutation();

  const [checkResultInProgressOfPacket] = useCheckResultInProgressOfPacketMutation();

  const [changeInterviewModeToClinician] = useChangeInterviewModeToClinicianMutation();

  const [changeMonitorModeToClinician] = useChangeMonitorModeToClinicianMutation();

  const [toggleInterviewResultActivation] = useToggleInterviewResultActivationMutation();

  const [toggleTrackerResultActivation] = useToggleTrackerResultActivationMutation();

  // const deleteReport = () => {
  //   dispatch(callShowPopup);
  //   handleClose();
  // };

  const packetContentPopup = {
    title: t('patient.patientDashboard.menuList.packetPopup.title'),
    description: t('patient.patientDashboard.menuList.packetPopup.description'),
    btnOk: t('patient.patientDashboard.menuList.packetPopup.btnOk'),
    btnClose: t('patient.patientDashboard.menuList.packetPopup.btnClose'),
  } as PopupContent;

  const viewReport = async (type: AssessmentType) => {
    dispatch(setPageStatus({ pageStatus: PageStatus.Loading }));
    handleClose();

    let data: DownloadReportRequest;
    if (type === AssessmentType.Interview) {
      data = {
        interviewResultId: assessmentId,
        latestVersion: true,
        type: AssessmentType.Interview,
      };
    } else {
      data = {
        monitorResultId: assessmentId,
        type: AssessmentType.Monitor,
      };
    }
    await startDownloadReport(data);
  };

  const isSingleAssessment = (
    checkResultInProgressOfPacketResponse: CheckResultInProgressOfPacketResponse
  ) =>
    checkResultInProgressOfPacketResponse &&
    !checkResultInProgressOfPacketResponse.success &&
    !checkResultInProgressOfPacketResponse.interviewResultId &&
    !checkResultInProgressOfPacketResponse.trackerResultId;

  const handleCheckResultInProgressOfPacket = async (
    isAllowToShowPacketPopup: boolean,
    type: AssessmentType,
    assessmentId: number
  ): Promise<CheckResultInProgressOfPacketResponse> => {
    const checkResultInProgressOfPacketResponse: CheckResultInProgressOfPacketResponse =
      await checkResultInProgressOfPacket({
        [type === AssessmentType.Interview ? 'interviewResultId' : 'trackerResultId']: assessmentId,
      }).unwrap();

    if (!isSingleAssessment(checkResultInProgressOfPacketResponse) && isAllowToShowPacketPopup) {
      setIsShowPacketPopup(true);
    }
    return checkResultInProgressOfPacketResponse;
  };

  const handleProviderAsksTheQuestions = async (assessmentId: number) => {
    handleClose();
    try {
      if (type === AssessmentType.Interview) {
        const changeInterviewModeToClinicianResponse: ChangeInterviewModeToClinicianResponse =
          await changeInterviewModeToClinician({
            interviewResultId: assessmentId,
          }).unwrap();
        if (changeInterviewModeToClinicianResponse?.newInterviewResultId) {
          clearAssessmentCachedData();
          refetchData();
          dispatch(setInterviewMode({ interviewMode: InterviewMode.Clinician }));
          navigate(
            `/dashboard/patient/${patientId}/${changeInterviewModeToClinicianResponse?.newInterviewResultId}/start-interview`,
            {
              state: { resumeInterview: false },
            }
          );
        }
      } else {
        const changeMonitorModeToClinicianResponse: ChangeMonitorModeToClinicianResponse =
          await changeMonitorModeToClinician({
            trackerResultId: assessmentId,
          }).unwrap();
        if (changeMonitorModeToClinicianResponse?.newTrackerResultId) {
          clearAssessmentCachedData();
          refetchData();
          dispatch(setMonitorMode({ monitorMode: MonitorModeName.Clinician }));
          navigate(
            `/dashboard/patient/${patientId}/${changeMonitorModeToClinicianResponse?.newTrackerResultId}/start-monitor`,
            {
              state: { resumeInterview: false },
            }
          );
        }
      }
    } catch (e) {
      dispatch(setPageStatus({ pageStatus: PageStatus.Idle }));
      toast.publish(
        t('patient.patientDashboard.form.intakePacketPopup.patientUsesThisDeviceError'),
        'error'
      );
    }
  };

  const handleRestartOrResumeInterview = async () => {
    handleClose();
    const { id, trial, type, status } = assessmentItem;
    try {
      const checkResultInProgressOfPacketResponse = await handleCheckResultInProgressOfPacket(
        true,
        type,
        id
      );
      if (isSingleAssessment(checkResultInProgressOfPacketResponse)) {
        if (status === AssessmentStatus.InProgress) {
          if (type === AssessmentType.Interview) {
            navigate(`/dashboard/patient/${patientId}/${id}/resume-interview`, {
              state: { resumeInterview: true },
            });
            dispatch(setTrialInterview({ trialInterview: trial || false }));
          } else {
            navigate(`/dashboard/patient/${patientId}/${id}/resume-monitor`, {
              state: { resumeMonitor: true },
            });
          }
        } else {
          if (type === AssessmentType.Interview) {
            navigate(`/dashboard/patient/${patientId}/${id}/start-interview`, {
              state: { resumeInterview: false },
            });
          } else {
            navigate(`/dashboard/patient/${patientId}/${id}/start-monitor`, {
              state: { resumeInterview: false },
            });
          }
        }
      } else {
        let resultType: AssessmentType = type;
        if (
          checkResultInProgressOfPacketResponse &&
          !checkResultInProgressOfPacketResponse.success
        ) {
          resultType = checkResultInProgressOfPacketResponse.interviewResultId
            ? AssessmentType.Interview
            : AssessmentType.Monitor;
        }

        const resultStatus = checkResultInProgressOfPacketResponse?.statusData || status;
        const resultTrial: boolean = (checkResultInProgressOfPacketResponse?.trial || trial)!;
        if (resultStatus === AssessmentStatus.InProgress) {
          if (resultType === AssessmentType.Interview) {
            setContinueHandler(() => {
              return () => {
                navigate(
                  `/dashboard/patient/${patientId}/${
                    checkResultInProgressOfPacketResponse.success
                      ? id
                      : checkResultInProgressOfPacketResponse.interviewResultId
                  }/resume-interview`,
                  {
                    state: { resumeInterview: true },
                  }
                );
                dispatch(setTrialInterview({ trialInterview: resultTrial || false }));
              };
            });
          } else {
            setContinueHandler(() => {
              return () => {
                navigate(
                  `/dashboard/patient/${patientId}/${
                    checkResultInProgressOfPacketResponse.success
                      ? id
                      : checkResultInProgressOfPacketResponse.trackerResultId
                  }/resume-monitor`,
                  {
                    state: { resumeMonitor: true },
                  }
                );
              };
            });
          }
        } else {
          if (resultType === AssessmentType.Interview) {
            setContinueHandler(() => {
              return () => {
                navigate(
                  `/dashboard/patient/${patientId}/${
                    checkResultInProgressOfPacketResponse.success
                      ? id
                      : checkResultInProgressOfPacketResponse.interviewResultId
                  }/start-interview`,
                  {
                    state: { resumeInterview: false },
                  }
                );
              };
            });
          } else {
            setContinueHandler(() => {
              return () => {
                navigate(
                  `/dashboard/patient/${patientId}/${
                    checkResultInProgressOfPacketResponse.success
                      ? id
                      : checkResultInProgressOfPacketResponse.trackerResultId
                  }/start-monitor`,
                  {
                    state: { resumeInterview: false },
                  }
                );
              };
            });
          }
        }
      }
    } catch (e) {
      logger.error(e);
    }
  };

  const handleEmailOrTextTheQuestions = async (status: string, assessmentId: number) => {
    handleClose();
    try {
      if (type === AssessmentType.Interview) {
        await sendInterviewEmailOrTextTheQuestions({
          interviewResultId: assessmentId,
        }).unwrap();
        toast.publish(
          t('patient.patientDashboard.form.intakePacketPopup.sendEmailOrTextTheQuestionsSuccess'),
          'success'
        );
      } else {
        await sendMonitorEmailOrTextTheQuestions({
          trackerResultId: assessmentId,
        }).unwrap();
        toast.publish(
          t('patient.patientDashboard.form.intakePacketPopup.sendEmailOrTextTheQuestionsSuccess'),
          'success'
        );
      }
    } catch (e) {
      dispatch(setPageStatus({ pageStatus: PageStatus.Idle }));
      toast.publish(
        t('patient.patientDashboard.form.intakePacketPopup.sendEmailOrTextTheQuestionsError'),
        'error'
      );
    }
  };

  const viewReportHistory = () => {
    const url = generatePath('/dashboard/patient/:patientId/report-history/:interviewResultId', {
      patientId,
      interviewResultId: assessmentId + '',
    });
    navigate(url);
  };

  const editSchedule = () => {
    const url = generatePath(
      '/dashboard/patient/:patientId/edit-schedule/:assessmentType/:assessmentTypeId',
      {
        patientId: patientId,
        assessmentType: type + '',
        assessmentTypeId: reducedTypeId.toString(),
      }
    );
    navigate(url);
  };

  const editInterview = () => {
    const url = generatePath('/dashboard/patient/:patientId/:interviewResultId/edit-interview', {
      patientId: patientId,
      interviewResultId: assessmentId + '',
    });
    navigate(url);
  };

  const handleUnlockReport = async () => {
    await unlockReport({
      interviewResultId: assessmentId,
      patientId,
    }).unwrap();

    handleClose();
    clearAssessmentCachedData();
    refetchData();

    toast.publish(t('interviewReport.success.unlockReport'), 'success');
  };

  const handleClickPacketPopup = async (): Promise<void> => {
    setIsShowPacketPopup(false);

    if (continueHandler) {
      await continueHandler();
    }
  };

  const handleClosePacketPopup = async (): Promise<void> => {
    setIsShowPacketPopup(false);
  };

  const showToggleActivationPopup = async (active: boolean) => {
    let content: PopupContent;
    if (type === AssessmentType.Interview) {
      content = active ? deactivateInterviewContentPopup : reactivateInterviewContentPopup;
    } else {
      content = active ? deactivateTrackerContentPopup : reactivateTrackerContentPopup;
    }
    setToggleActivationContent(content);
    setIsShowToggleActivationPopup(true);
    handleClose();
  };

  const handleToggleActivation = async (
    reason?: string,
    successCallback?: () => void
  ): Promise<void> => {
    try {
      if (type === AssessmentType.Interview) {
        await toggleInterviewResultActivation({ interviewResultId: assessmentId, reason }).unwrap();
      } else {
        await toggleTrackerResultActivation({ trackerResultId: assessmentId, reason }).unwrap();
      }
      if (successCallback) {
        successCallback();
      }
      setIsShowToggleActivationPopup(false);
      clearAssessmentCachedData();
      refetchData();
      toast.publish(toggleActivationContent?.toastMessage || '', 'success');
    } catch (e) {
      let errorMessage: string;
      if (type === AssessmentType.Interview) {
        errorMessage = assessmentItem.active
          ? t('patient.deactivateInterview.toastMessageFail')
          : t('patient.reactivateInterview.toastMessageFail');
      } else {
        errorMessage = assessmentItem.active
          ? t('patient.deactivateTracker.toastMessageFail')
          : t('patient.reactivateTracker.toastMessageFail');
      }
      toast.publish(errorMessage, 'error');
    }
  };

  const handleCloseToggleActivationPopup = async (): Promise<void> => {
    setIsShowToggleActivationPopup(false);
  };

  const menuItems = [
    {
      isDisplayed:
        isAssessmentCompleted ||
        isAssessmentEditing,
      label: t('patient.patientDashboard.menuList.viewReport'),
      icon: <EyeIcon />,
      onClick: () => viewReport(type),
    },
    {
      isDisplayed:
        canConductAssessment(currentUser.roleId) &&
        !isSignature &&
        patientStatus === PatientStatus.Active &&
        type === AssessmentType.Interview &&
        status !== AssessmentStatus.InProgress &&
        assessmentItem.active &&
        (editExpireTimeStart
          ? new Date().getTime() <
            new Date(
              new Date(editExpireTimeStart).setDate(
                new Date(editExpireTimeStart).getDate() + 14
              )
            ).getTime()
          : new Date().getTime() <
            new Date(
              new Date(completedAt).setDate(new Date(completedAt).getDate() + 14)
            ).getTime()),
      label: t('patient.patientDashboard.menuList.editReport'),
      icon: <EditPencilIcon />,
      onClick: editInterview,
    },
    {
      isDisplayed:
        (isAssessmentCompleted || isAssessmentEditing) &&
        type === AssessmentType.Interview,
      label: t('patient.patientDashboard.menuList.interviewHistory'),
      icon: <HistoryIcon />,
      onClick: viewReportHistory,
    },
    {
      isDisplayed:
        canConductAssessment(currentUser.roleId) &&
        patientStatus === PatientStatus.Active &&
        currentUser.roleId === Role.SuperAdmin &&
        type === AssessmentType.Interview &&
        assessmentItem.active &&
        editExpireTimeStart &&
        new Date().getTime() >
          new Date(
            new Date(editExpireTimeStart).setDate(
              new Date(editExpireTimeStart).getDate() + 14
            )
          ).getTime(),
      label: t('patient.patientDashboard.menuList.unlockReport'),
      icon: <LockOpen />,
      onClick: handleUnlockReport,
    },
    {
      isDisplayed:
        canConductAssessment(currentUser.roleId) &&
        patientStatus === PatientStatus.Active &&
        assessmentItem.active &&
        (status === AssessmentStatus.NotStarted ||
          (status === AssessmentStatus.InProgress &&
            (assessmentItem.assessmentMode === AssessmentMode.Clinician ||
              assessmentItem.assessmentMode === AssessmentMode.ExternalClinician))),
      label: t('patient.patientDashboard.menuList.providerAsksTheQuestions'),
      icon: <QuestionMarkOutlinedIcon />,
      onClick: () => {
        if (
          status === AssessmentStatus.NotStarted &&
          (assessmentItem.assessmentMode === AssessmentMode.Patient ||
            assessmentItem.assessmentMode === AssessmentMode.ExternalPatient ||
            assessmentItem.assessmentMode === AssessmentMode.Schedule)
        ) {
          handleProviderAsksTheQuestions(assessmentId);
        } else if (
          (status === AssessmentStatus.NotStarted ||
            status === AssessmentStatus.InProgress) &&
          (assessmentItem.assessmentMode === AssessmentMode.Clinician ||
            assessmentItem.assessmentMode === AssessmentMode.ExternalClinician)
        ) {
          handleRestartOrResumeInterview();
        }
      },
    },
    {
      isDisplayed:
        canConductAssessment(currentUser.roleId) &&
        patientStatus === PatientStatus.Active &&
        assessmentItem.active &&
        !!(patientInfo?.email || patientInfo?.cellPhoneNumber) &&
        assessmentItem.assessmentMode !== AssessmentMode.Clinician &&
        assessmentItem.assessmentMode !== AssessmentMode.ExternalClinician &&
        (status === AssessmentStatus.NotStarted || status === AssessmentStatus.InProgress),
      label: t('patient.patientDashboard.menuList.emailOrTextTheQuestions'),
      icon: <EmailOutlinedIcon />,
      onClick: () => handleEmailOrTextTheQuestions(status, assessmentId),
    },
    {
      isDisplayed:
        canConductAssessment(currentUser.roleId) &&
        patientStatus === PatientStatus.Active &&
        assessmentItem.active &&
        assessmentItem.assessmentMode !== AssessmentMode.ExternalClinician &&
        assessmentItem.assessmentMode !== AssessmentMode.Clinician &&
        (status === AssessmentStatus.NotStarted || status === AssessmentStatus.InProgress),
      label: t('patient.patientDashboard.menuList.patientUsesThisDevice'),
      icon: <DevicesOutlinedIcon />,
      onClick: handleRestartOrResumeInterview,
    },
    {
      isDisplayed:
        canConductAssessment(currentUser.roleId) &&
        patientStatus === PatientStatus.Active && reducedTypeId! > 0 && hasInterval === true,
      label: t('patient.patientDashboard.menuList.editSchedule'),
      icon: <EditInterviewIcon />,
      onClick: editSchedule,
    },
    {
      isDisplayed:
        canConductAssessment(currentUser.roleId) &&
        patientStatus === PatientStatus.Active &&
        canDeactivateOrReactivateInterview(currentUser.roleId) &&
        status !== AssessmentStatus.PendingScoring,
      label: assessmentItem.active
        ? type === AssessmentType.Monitor
          ? t('patient.patientDashboard.menuList.deactivateMonitor')
          : t('patient.patientDashboard.menuList.deactivateInterview')
        : type === AssessmentType.Monitor
        ? t('patient.patientDashboard.menuList.reactivateMonitor')
        : t('patient.patientDashboard.menuList.reactivateInterview'),
      icon: assessmentItem.active ? <ToggleOffIcon /> : <ToggleOnIcon />,
      onClick: () => showToggleActivationPopup(assessmentItem.active || false),
    },
    // {
    //   isDisplayed:
    //     canConductAssessment(currentUser.roleId) &&
    //     patientStatus === PatientStatus.Active &&
    //     status !== AssessmentStatus.PendingScoring &&
    //     canDeleteInterviewOrPatient(currentUser.roleId),
    //   label: t('patient.patientDashboard.menuList.deleteReport'),
    //   icon: <DeleteIcon />,
    //   onClick: deleteReport,
    // },
  ];

  const hasMenuItems = menuItems.some((item) => item.isDisplayed);

  return hasMenuItems ? (
    <div>
      <PopupModal
        isOpen={isShowPacketPopup}
        contentPopup={packetContentPopup}
        onClick={handleClickPacketPopup}
        onClose={handleClosePacketPopup}
      />
      <PopupModal
        isOpen={isShowToggleActivationPopup}
        contentPopup={toggleActivationContent}
        onClick={handleToggleActivation}
        onClose={handleCloseToggleActivationPopup}
      />
      <ContextMenu
        id={`assessment-${assessmentId}`}
        anchorEl={anchorEl}
        handleClose={handleClose}
        handleClick={handleClick}
        menuItems={menuItems}
      />
    </div>
  ) : null;
}
